import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';

const BillingTerms: React.FC = () => {
  const theme = useTheme();
  const lastUpdated = 'February 17, 2026';

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Subscription & Billing Terms
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Last updated: {lastUpdated}
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: `1px solid ${theme.palette.divider}` }}>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These Subscription & Billing Terms ("Billing Terms") govern paid access to the Tier One Content Service
            operated by Jakatech LLC d/b/a Tier One Content ("Jakatech," "we," "us," or "our"). These Billing Terms
            are incorporated into the{' '}
            <MuiLink component={Link} to="/legal/terms">
              Terms of Service
            </MuiLink>
            .
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            1. Subscription Plans
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Details of available subscription plans, features, and pricing are published on our{' '}
            <MuiLink component={Link} to="/pricing">
              pricing page
            </MuiLink>
            .
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may modify plan features from time to time to improve the Service, provided that material reductions
            in paid features will be communicated in advance.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Subscriptions may be purchased by individuals or Organizations. If purchased by an Organization, the
            Organization is the account holder and is responsible for payment obligations.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            2. Free Trials (If Offered)
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If we offer a free trial:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Trial duration will be specified at signup.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Payment information may be required to begin the trial.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Unless cancelled before the trial ends, the subscription will automatically convert to a paid plan.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                No charges will be made during the trial period unless stated otherwise.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We reserve the right to modify or terminate trials at any time.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            3. Billing & Payment
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Subscriptions are billed in advance on a monthly or annual basis, as selected at purchase.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Payments are processed through Stripe or other authorized third-party payment providers.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You authorize us to charge:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Recurring subscription fees
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Applicable taxes
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Add-ons or overage fees (if applicable)
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You must maintain valid payment information.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            4. Automatic Renewal
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Subscriptions renew automatically at the end of each billing cycle unless cancelled prior to renewal.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            By purchasing a subscription, you authorize recurring billing until cancellation.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may cancel at any time through your account settings.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Cancellation takes effect at the end of the current billing cycle.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            5. Upgrades, Downgrades & Plan Changes
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you upgrade your subscription:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Additional charges may be applied immediately.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Fees may be prorated for the remainder of the billing period.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you downgrade:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Downgrades take effect at the next billing cycle.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                No refunds are provided for unused portions of the current billing period.
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            6. Cancellations & Refunds
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may cancel your subscription at any time.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Except where required by applicable law:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Subscription fees are non-refundable.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                We do not provide refunds for partial billing periods.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                No refunds are issued for unused accounts.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If required by consumer protection laws, refunds will be provided accordingly.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            7. Late Payments & Suspension
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If payment is overdue:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                We may provide a grace period at our discretion.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                We may suspend or restrict access to the Service.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Continued non-payment may result in termination.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You remain responsible for all outstanding fees.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may recover reasonable costs of collection, including legal fees, where permitted by law.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            8. Taxes
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Prices exclude applicable taxes unless otherwise stated.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You are responsible for:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Sales tax
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                VAT
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                GST
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Digital services taxes
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Where required, we will collect and remit applicable taxes.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you are a business customer claiming tax exemption, you must provide valid documentation (e.g., VAT ID).
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            9. Price Changes
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may modify subscription pricing with at least 30 days' notice.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Price changes will apply at the next renewal period.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you do not agree to new pricing, you may cancel before renewal.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            10. Chargebacks & Payment Disputes
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you initiate a chargeback or payment dispute without valid grounds:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                We may suspend your account.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                You remain responsible for outstanding amounts.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                We may recover associated processing fees.
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            11. Termination for Non-Payment
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may terminate subscriptions for continued non-payment.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Upon termination:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Access to paid features will cease.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Data retention and deletion will be governed by our{' '}
                <MuiLink component={Link} to="/legal/terms">
                  Terms of Service
                </MuiLink>
                .
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            12. Contact
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            For billing inquiries:
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Jakatech LLC<br />
            d/b/a Tier One Content<br />
            Email: support@tieronecontent.com
          </Typography>

          <Divider sx={{ my: 5 }} />

          <Typography variant="body2" color="text.secondary">
            These Subscription & Billing Terms were last updated on {lastUpdated}. For questions about
            billing, please contact support@tieronecontent.com.
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default BillingTerms;
